import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X, SpellCheck, Lightbulb, Plus } from 'lucide-react';
import { overlayDismiss } from '../../../lib/overlayDismiss';

/**
 * KeywordTuningModal — 辨識關鍵字 + 發音校正（兩塊）
 *
 *   點開先帶出 avatar 既有的 keyword / voiceCorrectionWords（由父層從 avatar.raw 傳入），
 *   新增 / 移除後 sendMessage 觸發 tune-ivr-vad-keywords skill 一次寫回兩個 top-level 欄位
 *   並清 avatar-{spaceId} cache。
 *
 *   keyword 影響 ASR + 錄音逐字稿（讓同音品牌字不被轉錯，例：宏華→紅華）；
 *   voiceCorrectionWords 只影響 TTS 念字（例：中壢→中利）。
 */
export function KeywordTuningModal({
    open, onClose, onApply, isStreaming, avatarName,
    currentKeyword = [], currentVoiceCorrectionWords = [],
}) {
    const { t } = useTranslation('wsModals');
    const [keywords, setKeywords] = useState([]);
    const [corrections, setCorrections] = useState([]);
    const [kwDraft, setKwDraft] = useState('');
    const [origDraft, setOrigDraft] = useState('');
    const [replDraft, setReplDraft] = useState('');

    // 開啟時帶出既有值（深拷貝避免直接改 props）
    useEffect(() => {
        if (!open) return;
        setKeywords(Array.isArray(currentKeyword) ? currentKeyword.filter(Boolean) : []);
        setCorrections(
            Array.isArray(currentVoiceCorrectionWords)
                ? currentVoiceCorrectionWords
                    .filter((c) => c && c.originalText && c.replacementText)
                    .map((c) => ({ originalText: c.originalText, replacementText: c.replacementText }))
                : [],
        );
        setKwDraft('');
        setOrigDraft('');
        setReplDraft('');
    }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

    if (!open) return null;

    const addKeyword = () => {
        const v = kwDraft.trim();
        if (!v || keywords.includes(v)) { setKwDraft(''); return; }
        setKeywords((arr) => [...arr, v]);
        setKwDraft('');
    };
    const removeKeyword = (idx) => setKeywords((arr) => arr.filter((_, i) => i !== idx));

    const addCorrection = () => {
        const o = origDraft.trim();
        const r = replDraft.trim();
        if (!o || !r) return;
        if (corrections.some((c) => c.originalText === o)) { setOrigDraft(''); setReplDraft(''); return; }
        setCorrections((arr) => [...arr, { originalText: o, replacementText: r }]);
        setOrigDraft('');
        setReplDraft('');
    };
    const removeCorrection = (idx) => setCorrections((arr) => arr.filter((_, i) => i !== idx));

    const canApply = !isStreaming;

    const handleApply = () => {
        if (!canApply) return;

        const kwLine = keywords.length ? keywords.join('、') : '（清空，設為空陣列）';
        const corrLine = corrections.length
            ? corrections.map((c) => `${c.originalText} → ${c.replacementText}`).join('\n')
            : '（清空，設為空陣列）';

        const prompt = `請用 tune-ivr-vad-keywords skill 更新這個 IVR 角色 (${avatarName || 'avatar'}) 的「辨識關鍵字」與「發音校正」設定。這兩個都是 avatar doc 的 top-level 欄位，用同一支 skill 一次寫回。

# 辨識關鍵字（avatar.keyword，array of string）
給語音辨識 / 錄音逐字稿的專有詞庫提示（人名 / 地名 / 產品 / 品牌），請整批覆寫成以下完整清單：
${kwLine}

# 發音校正（avatar.voiceCorrectionWords，array of {originalText, replacementText}）
TTS 念字校正（原字 → 念成），請整批覆寫成以下完整清單：
${corrLine}

要求：
1. 兩個欄位都**整批覆寫（set，不是 arrayUnion）**成上面清單，不要保留我沒列出的舊值、也不要自行增刪
2. keyword 影響 ASR 與錄音逐字稿（讓同音品牌字不被轉錯）；voiceCorrectionWords 只影響 TTS 念字
3. ⚠️ 寫回後務必清 avatar 的 Redis cache：\`avatar-{spaceId}\`、\`avatar-all\`（這兩個欄位快取在這裡，不清的話 live 端會讀到舊值）
4. 完成後用商家口吻摘要改了什麼（不要貼 JSON）`;

        const displayText = `${t('keyword.display.title')}\n${t('keyword.kw.title')}：${keywords.length ? keywords.join('、') : '—'}\n${t('keyword.vc.title')}：${corrections.length ? corrections.map((c) => `${c.originalText}→${c.replacementText}`).join('、') : '—'}`;

        onApply(prompt, { displayText });
        onClose();
    };

    return (
        <div className="fixed inset-0 bg-ink-900/30 backdrop-blur-sm z-50 grid place-items-center p-4" {...overlayDismiss(onClose)}>
            <div
                className="bg-cream-50 border border-cream-200 rounded-lg shadow-xl w-full max-w-2xl max-h-[92vh] flex flex-col"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-cream-200">
                    <div>
                        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-400 inline-flex items-center gap-1.5">
                            <SpellCheck size={11} /> keyword / pronunciation
                        </div>
                        <h2 className="heading-display text-lg font-semibold text-ink-900 mt-0.5">{t('keyword.title')}</h2>
                    </div>
                    <button
                        onClick={onClose}
                        className="size-8 inline-flex items-center justify-center rounded-md text-ink-400 hover:text-ink-700 hover:bg-cream-100 transition"
                    >
                        <X size={16} />
                    </button>
                </div>

                {/* Body */}
                <div className="px-6 py-5 flex-1 overflow-y-auto space-y-6">
                    {/* ── 區塊 1：辨識關鍵字 ── */}
                    <section>
                        <div className="flex items-center justify-between mb-2">
                            <div className="font-mono text-[10px] uppercase tracking-wider text-ink-400 inline-flex items-center gap-1.5">
                                {t('keyword.kw.title')}
                                {keywords.length > 0 && (
                                    <span className="px-1.5 py-px rounded-full bg-cream-200 text-ink-500 text-[10px] tabular-nums normal-case tracking-normal">{keywords.length}</span>
                                )}
                            </div>
                            {keywords.length > 0 && (
                                <button
                                    onClick={() => setKeywords([])}
                                    className="text-[11px] text-ink-400 hover:text-accent-700 transition"
                                >
                                    {t('keyword.clearAll')}
                                </button>
                            )}
                        </div>
                        <div className="rounded-md bg-cream-100/60 border border-cream-200 px-3 py-2 text-xs text-ink-500 flex items-start gap-2 mb-3">
                            <Lightbulb size={14} strokeWidth={1.6} className="text-accent-700 shrink-0 mt-0.5" />
                            <div className="leading-relaxed">{t('keyword.kw.info')}</div>
                        </div>

                        {keywords.length > 0 ? (
                            <div className="flex flex-wrap gap-1.5 mb-3 max-h-36 overflow-y-auto rounded-md bg-cream-100/40 border border-cream-200 p-2">
                                {keywords.map((kw, idx) => (
                                    <span
                                        key={`${kw}-${idx}`}
                                        className="inline-flex items-center gap-1 pl-2.5 pr-1.5 py-1 rounded-full bg-white border border-cream-300 text-sm text-ink-800 max-w-full"
                                    >
                                        <span className="truncate">{kw}</span>
                                        <button
                                            onClick={() => removeKeyword(idx)}
                                            className="size-4 shrink-0 inline-flex items-center justify-center rounded-full text-ink-300 hover:text-ink-700 hover:bg-cream-100 transition"
                                        >
                                            <X size={11} />
                                        </button>
                                    </span>
                                ))}
                            </div>
                        ) : (
                            <div className="text-xs text-ink-300 mb-3">{t('keyword.kw.empty')}</div>
                        )}

                        <div className="flex gap-2">
                            <input
                                value={kwDraft}
                                onChange={(e) => setKwDraft(e.target.value)}
                                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addKeyword(); } }}
                                placeholder={t('keyword.kw.placeholder')}
                                className="flex-1 px-3 py-2 text-sm border border-cream-300 focus:border-accent-500 outline-none rounded-md bg-white"
                            />
                            <button
                                onClick={addKeyword}
                                disabled={!kwDraft.trim()}
                                className="px-3 py-2 text-sm rounded-md bg-cream-100 text-ink-700 hover:bg-cream-200 transition inline-flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                                <Plus size={14} /> {t('keyword.kw.addBtn')}
                            </button>
                        </div>
                    </section>

                    {/* ── 區塊 2：發音校正 ── */}
                    <section>
                        <div className="flex items-center justify-between mb-2">
                            <div className="font-mono text-[10px] uppercase tracking-wider text-ink-400 inline-flex items-center gap-1.5">
                                {t('keyword.vc.title')}
                                {corrections.length > 0 && (
                                    <span className="px-1.5 py-px rounded-full bg-cream-200 text-ink-500 text-[10px] tabular-nums normal-case tracking-normal">{corrections.length}</span>
                                )}
                            </div>
                            {corrections.length > 0 && (
                                <button
                                    onClick={() => setCorrections([])}
                                    className="text-[11px] text-ink-400 hover:text-accent-700 transition"
                                >
                                    {t('keyword.clearAll')}
                                </button>
                            )}
                        </div>
                        <div className="rounded-md bg-cream-100/60 border border-cream-200 px-3 py-2 text-xs text-ink-500 flex items-start gap-2 mb-3">
                            <Lightbulb size={14} strokeWidth={1.6} className="text-accent-700 shrink-0 mt-0.5" />
                            <div className="leading-relaxed">{t('keyword.vc.info')}</div>
                        </div>

                        {corrections.length > 0 ? (
                            <div className="space-y-1.5 mb-3 max-h-44 overflow-y-auto rounded-md bg-cream-100/40 border border-cream-200 p-2">
                                {corrections.map((c, idx) => (
                                    <div key={`${c.originalText}-${idx}`} className="flex items-center gap-2 text-sm">
                                        <span className="flex-1 min-w-0 truncate px-2.5 py-1.5 rounded-md bg-white border border-cream-300 text-ink-800">{c.originalText}</span>
                                        <span className="text-ink-300 font-mono text-xs shrink-0">→</span>
                                        <span className="flex-1 min-w-0 truncate px-2.5 py-1.5 rounded-md bg-white border border-cream-300 text-ink-800">{c.replacementText}</span>
                                        <button
                                            onClick={() => removeCorrection(idx)}
                                            className="size-7 shrink-0 inline-flex items-center justify-center rounded-md text-ink-300 hover:text-ink-700 hover:bg-cream-100 transition"
                                        >
                                            <X size={13} />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="text-xs text-ink-300 mb-3">{t('keyword.vc.empty')}</div>
                        )}

                        <div className="flex items-center gap-2">
                            <input
                                value={origDraft}
                                onChange={(e) => setOrigDraft(e.target.value)}
                                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addCorrection(); } }}
                                placeholder={t('keyword.vc.origPlaceholder')}
                                className="flex-1 px-3 py-2 text-sm border border-cream-300 focus:border-accent-500 outline-none rounded-md bg-white"
                            />
                            <span className="text-ink-300 font-mono text-xs">→</span>
                            <input
                                value={replDraft}
                                onChange={(e) => setReplDraft(e.target.value)}
                                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addCorrection(); } }}
                                placeholder={t('keyword.vc.replPlaceholder')}
                                className="flex-1 px-3 py-2 text-sm border border-cream-300 focus:border-accent-500 outline-none rounded-md bg-white"
                            />
                            <button
                                onClick={addCorrection}
                                disabled={!origDraft.trim() || !replDraft.trim()}
                                className="px-3 py-2 text-sm rounded-md bg-cream-100 text-ink-700 hover:bg-cream-200 transition inline-flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                                <Plus size={14} /> {t('keyword.vc.addBtn')}
                            </button>
                        </div>
                    </section>
                </div>

                {/* Footer */}
                <div className="flex items-center justify-end gap-2 px-6 py-3 border-t border-cream-200">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 text-sm rounded-md text-ink-700 hover:bg-cream-100 transition"
                    >
                        {t('common:actions.cancel')}
                    </button>
                    <button
                        onClick={handleApply}
                        disabled={!canApply}
                        className="px-4 py-2 text-sm rounded-md bg-ink-900 text-white hover:bg-ink-700 transition disabled:bg-cream-300 disabled:cursor-not-allowed"
                    >
                        {t('keyword.applyBtn')}
                    </button>
                </div>
            </div>
        </div>
    );
}
